import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import _ from "lodash";
import {
  Grid,
  GridColumn as Column,
  GridToolbar,
} from "@progress/kendo-react-grid";
import {
  CustomActionsCell,
  CustomCell,
  HeaderCustomCell,
  initialDataState,
  ModifyConfigurableTaskData,
} from "./configurable-task-util";
import { processWithGroups } from "./configurable-task-process";
import {
  buildColumnTitleMap,
  resolveConfigurableColumnTitle,
  resolveConfigurableExcelColumnTitle,
} from "./configurable-task-column-titles";
import { process } from "@progress/kendo-data-query";
import { TaskGridFilter } from "../common-components/task-grid-filter";
import { FluentInput } from "../../../../../components/FluentUI/Fluent-Input";
import { FluentButton } from "../../../../../components/FluentUI/Fluent-Button";
import FluentUiIcon from "../../../../../assets/fluent-ui-icon";
import {
  ExcelExport,
  ExcelExportColumn,
} from "@progress/kendo-react-excel-export";
import {
  setExpandedState,
  getGroupIds,
} from "@progress/kendo-react-data-tools";
import { useFluentToast } from "../../../../../components/FluentUI/Fluent-Toast";
import { GetTaskForConfigurableTelerikGrid } from "../../../../../services/api/task-telerik-api";
import { LoginContext } from "../../../../../services/api/authentication-api";
import TaskFormsDetailModal from "./task-forms-detail-modal";

// New Grid Component for Configurable Task List
// To support the new use cases. E.g. RBC-S&D.
export const ConfigurableTaskGrid = ({ data, type, GetTasks, taskToOpen, setTaskToOpen }) => {
  const _export = useRef(null);
  const user = useContext(LoginContext);
  const labels = user.currentUser?.objectLabels;

  const [showTask, setShowTask] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedTaskData, setSelectedTaskData] = useState(null);
  const [selectedTaskRow, setSelectedTaskRow] = useState(null);
  const [loadingTask, setLoadingTask] = useState(false);

  const savedGridDefination = JSON.parse(
    localStorage.getItem("gridState_" + user?.userID)
  );
  const savedGridColumnDefination = JSON.parse(
    localStorage.getItem("gridColumns_" + user?.userID)
  );
  const [savedSearch, setSavedSearch] = useState(
    localStorage.getItem("gridSearch_" + user?.userID) ?? ""
  );

  const [dataState, setDataState] = useState(
    savedGridDefination ?? initialDataState
  );
  const [dataResult, setDataResult] = useState(process([], dataState));
  const [collapsedState, setCollapsedState] = useState([]);
  const [columns, setColumns] = useState(
    savedGridColumnDefination ?? data.allColumns
  );

  const { showToast, FluentToast } = useFluentToast();

  const columnTitleMap = useMemo(
    () => buildColumnTitleMap(data?.columnDefinitions),
    [data?.columnDefinitions]
  );

  const onDataStateChange = (e) => {
    if (e.dataState.group) {
      e.dataState.group = e.dataState.group.filter(
        (obj, index) =>
          e.dataState.group.findIndex((item) => item.field === obj.field) ===
          index
      );
    }
    let tempSkip = e.dataState.skip;
    e.dataState.skip = 0;
    localStorage.setItem(
      "gridState_" + user?.userID,
      JSON.stringify(e.dataState)
    );
    e.dataState.skip = tempSkip;
    setDataState(e.dataState);
    setDataResult(processWithGroups(data.data, e.dataState));
  };

  const onExpandChange = useCallback(
    (event) => {
      const item = event.dataItem;
      if (item.groupId) {
        const collapsedIds = !event.value
          ? [...collapsedState, item.groupId]
          : collapsedState.filter((groupId) => groupId !== item.groupId);
        setCollapsedState(collapsedIds);
      }
    },
    [collapsedState]
  );

  const onColumnReorder = (event) => {
    let col = event.columns
      .filter((column) => column.title !== "Actions")
      .sort((a, b) => a.orderIndex - b.orderIndex)
      .map((column) => column.field.split(".")[0]);
    setColumns(col);
    localStorage.setItem("gridColumns_" + user?.userID, JSON.stringify(col));
  };

  const onColumnResize = (event) => {
    let col = event.columns
      .filter((column) => column.title !== "Actions")
      .sort((a, b) => a.orderIndex - b.orderIndex)
      .map((column) => column.field.split(".")[0]);
    setColumns(col);
    localStorage.setItem("gridColumns_" + user?.userID, JSON.stringify(col));
  };

  const onGroupsToggle = useCallback(() => {
    const dataStateGroups = processWithGroups(data.data, {
      group: dataState.group,
    });
    setCollapsedState(
      collapsedState.length ? [] : getGroupIds({ data: dataStateGroups.data })
    );
  }, [collapsedState, data.data, dataState]);

  const expandedResult = setExpandedState({
    data: dataResult.data,
    collapsedIds: collapsedState,
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps -- dataState changes are handled by onDataStateChange; re-run only when data prop changes
  useEffect(() => {
    if (data?.data?.length > 0) {
      setDataResult(processWithGroups(data.data, dataState));
    } else {
      setDataResult(process([], dataState));
    }
  }, [data]);

  const resetDataState = () => {
    setDataState(initialDataState);
    setDataResult(processWithGroups(data.data, initialDataState));
    setColumns(data.allColumns);
    setSavedSearch("");
    localStorage.removeItem("gridState_" + user?.userID);
    localStorage.removeItem("gridColumns_" + user?.userID);
    localStorage.removeItem("gridSearch_" + user?.userID);
  };

  const FilterData = (e) => {
    let value = e.target.value;
    let modifiedDataState = { ...dataState };

    let searchFilter = [];

    if (value !== "") {
      searchFilter = data.allColumns.map((column) => ({
        field: `${column}.SearchableData`,
        operator: "contains",
        value: value,
      }));

      searchFilter.push({
        field: "SearchableMembers.SearchableData",
        operator: "contains",
        value: value,
      });
    }

    modifiedDataState.filter = {
      logic: "or",
      filters: searchFilter,
    };
    let tempSkip = modifiedDataState.skip;
    modifiedDataState.skip = 0;
    localStorage.setItem(
      "gridState_" + user?.userID,
      JSON.stringify(modifiedDataState)
    );
    localStorage.setItem("gridSearch_" + user?.userID, value);
    modifiedDataState.skip = tempSkip;
    setDataState(modifiedDataState);
    setDataResult(processWithGroups(data.data, modifiedDataState));
  };

  const debouncedFilter = _.debounce(FilterData, 200);

  const formatExportData = (dataItems, labels) => {
    return dataItems.map(item => {
      const formattedItem = { ...item };

      if (labels) {
        if (formattedItem.LastActivityObject && formattedItem.LastActivityObject.SearchableData ) {
          let lastActivity = formattedItem.LastActivityObject.SearchableData;
          if (labels.ProjectSingular) {
            lastActivity = lastActivity.replaceAll("Project", labels.ProjectSingular);
          }
          if (labels.TaskSingular) {
            lastActivity = lastActivity.replaceAll("Task", labels.TaskSingular);
          }
          if (labels.FormSingular) {
            lastActivity = lastActivity.replaceAll("Form", labels.FormSingular);
          }
          formattedItem.LastActivityObject.SearchableData = lastActivity;
        }
      }

      return formattedItem;
    });
  };
  const excelExport = async () => {
    if (_export.current !== null) {
      let exportData = { data: data?.data, total: data?.data.length };

      if (dataState.filter || dataState.group) {
        let dState = { ...dataState };
        dState.take = dataResult.length;
        exportData = process(data?.data, dState);
      }

      if (dataState.group && exportData.data) {
        const flattenGroupedData = (group) =>
          group.reduce((acc, item) => {
            if (item.items) {
              return acc.concat(flattenGroupedData(item.items));
            }
            return acc.concat(item);
          }, []);

        exportData.data = flattenGroupedData(exportData.data);
      }
      exportData.data = formatExportData(exportData.data, labels);
      await _export.current.save(exportData);
    }
  };

  const onHide = () => {
    setShowTask(false);
    setSelectedTask(null);
    setSelectedTaskData(null);
    setSelectedTaskRow(null);
  };

  const onRowClick = async (event) => {
    const { dataItem } = event;
    const taskId = dataItem?.Id?.Data;
    if (!taskId) return;
    setSelectedTask(taskId);
    setSelectedTaskRow(dataItem);
    await getTask(taskId);
  };

  const getTask = useCallback(async (taskId) => {
    if (!taskId) return;
    setLoadingTask(true);
    setShowTask(true);
    try {
        let result = await GetTaskForConfigurableTelerikGrid(taskId);
        let modifiedResult = await ModifyConfigurableTaskData(
          result?.data?.data,
          user?.currentUser?.department?.DateFormatID
        );

        setSelectedTaskData((prev) => ({
          ...prev,
          data: modifiedResult,
          allColumns: result?.data.allColumns,
          task: result?.data.task,
        }));
    } catch (error) {
      console.error("Error fetching task:", error);
      showToast("Error", "Error fetching task data", "error");
    } finally {
      setLoadingTask(false);
    }
  }, [showToast, user]);

  useEffect(() => {
    // If a task should be opened, trigger the modal
    if (taskToOpen && data && data?.isLoading === false) {
      setSelectedTask(taskToOpen);
      getTask(taskToOpen);
      setTaskToOpen(null); // Reset after opening
    }
  }, [taskToOpen, setTaskToOpen, getTask]);

  return (
    <React.Fragment>
      <div className="configurable-list-container">
        <ExcelExport data={dataResult.data} ref={_export}>
          {data?.allColumns?.map((column) => {
            let newColumn =
              column === "Assigned Members" ? "SearchableMembers" : column;
            return (
              <ExcelExportColumn
                key={newColumn}
                field={`${newColumn}.SearchableData`}
                title={resolveConfigurableExcelColumnTitle(
                  column,
                  columnTitleMap,
                  data?.data?.[0]
                )}
              />
            );
          })}
        </ExcelExport>
        <Grid
          data={expandedResult}
          pageable={{
            info: true,
            pageSizes: [10, 25, 50, 75, 100],
          }}
          total={dataResult.total}
          onRowClick={onRowClick}
          groupable={true}
          reorderable={true}
          resizable={true}
          {...dataState}
          onDataStateChange={onDataStateChange}
          onColumnReorder={onColumnReorder}
          onColumnResize={onColumnResize}
          expandField="expanded"
          onExpandChange={onExpandChange}
          cells={{
            group: {
              groupHeader: (props) => HeaderCustomCell(props, user),
            },
          }}
        >
          <GridToolbar>
            <div className="task-grid-toolbar">
              <div className="toolbar-left">
                <FluentInput
                  placeholder={`Search in all ${type} ${labels?.TaskPlural ?? 'Tasks'}`}
                  defaultValue={savedSearch}
                  contentBefore={
                    <FluentUiIcon
                      name={"SearchRegular"}
                      color={"#0078D4"}
                      size={14}
                    />
                  }
                  onChange={(e) => debouncedFilter(e)}
                />
                <FluentButton
                  text="Export to XLS"
                  onClick={excelExport}
                />
              </div>
              <div className="toolbar-right">
                <FluentButton
                  text="Reset to Default"
                  icon={
                    <FluentUiIcon
                      name={"ArrowResetRegular"}
                      color={"#242424"}
                      size={20}
                    />
                  }
                  onClick={resetDataState}
                />
                <FluentButton
                  text="Refresh"
                  icon={
                    <FluentUiIcon
                      name={"ArrowClockwiseRegular"}
                      color={"#242424"}
                      size={20}
                    />
                  }
                  onClick={GetTasks}
                />
              </div>
            </div>
          </GridToolbar>
          {columns?.map(
            (column, index) =>
              // data?.data?.[0]?.[column]?.Hidden === false && ( //Remove in March
                <Column
                  key={column}
                  field={`${column}.Data`}
                  orderIndex={index}
                  title={resolveConfigurableColumnTitle(
                    column,
                    columnTitleMap,
                    data?.data?.[0]
                  )}
                  locked={data?.data?.[0]?.[column]?.Locked}
                  groupable={data?.data?.[0]?.[column]?.Groupable}
                  filter="date"
                  columnMenu={
                    data?.data?.[0]?.[column]?.Filterable &&
                    data?.data?.[0]?.[column]?.Sortable
                      ? (props) => {
                          return TaskGridFilter(props, data);
                        }
                      : null
                  }
                  cells={{
                    data: (props) => CustomCell(column, props),
                  }}
                />
              // )
          )}
          <Column
            orderIndex={columns?.length ?? 0}
            reorderable={false}
            field=""
            title="Actions"
            cells={{
              data: (props) => CustomActionsCell(props, GetTasks),
            }}
          />
        </Grid>
      </div>
      {showTask && selectedTask && (
        <TaskFormsDetailModal
          show={showTask}
          onHide={onHide}
          taskId={selectedTask}
          taskData={selectedTaskData}
          selectedTaskRow={selectedTaskRow}
          GetTasks={GetTasks}
          loading={loadingTask}
          isArchived={selectedTaskData?.task?.[0]?.isArchived === true}
          taskStatusId={selectedTaskData?.task?.[0]?.StatusId}
        />
      )}
      {FluentToast}
    </React.Fragment>
  );
};

ConfigurableTaskGrid.propTypes = {
  data: PropTypes.object.isRequired,
  type: PropTypes.string.isRequired,
  GetTasks: PropTypes.func.isRequired,
  taskToOpen: PropTypes.number,
  setTaskToOpen: PropTypes.func,
};
